import React, { useState } from 'react'
import { connect } from 'dva'
import PropTypes from 'prop-types'

import { findOrderViolations, formatLayerRows, getLayerRows, LAYER_ZONES } from '../../../lib/layerOrderMonitor'
import SidebarBody from '../SidebarBody'
import SidebarDetailHeader from '../SidebarDetailHeader'
import styles from './LayerOrder.module.scss'

const ZONE_LABELS = {
  [LAYER_ZONES.band]: 'band',
  [LAYER_ZONES.pinned]: 'pinned',
  [LAYER_ZONES.terrain]: 'terrain',
  [LAYER_ZONES.ui]: 'ui'
}

function LayerOrderSidebar(props) {
  const [copied, setCopied] = useState(false)
  const [rows, setRows] = useState(null)
  const [violations, setViolations] = useState([])
  const [visibleOnly, setVisibleOnly] = useState(false)

  if (!props.layerOrderEnabled) {
    return <Unavailable message='Layer Order is not enabled for this environment.' />
  }

  const read = (nextVisibleOnly = visibleOnly) => {
    const nextRows = getLayerRows(props.mapRef, nextVisibleOnly)
    setRows(nextRows)
    setViolations(findOrderViolations(nextRows))
    setCopied(false)
  }

  const handleToggleVisibleOnly = () => {
    const next = !visibleOnly
    setVisibleOnly(next)
    if (rows) read(next)
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatLayerRows(rows, violations))
      setCopied(true)
    } catch (error) {
      console.error('Could not copy layer order: ', error)
    }
  }

  if (!props.mapRef) {
    return <Unavailable message='Map is not ready yet.' />
  }

  const bandCount = rows ? rows.filter(row => row.zone === LAYER_ZONES.band).length : 0

  return (
    <>
      <SidebarDetailHeader title='Layer Order' />
      <SidebarBody className={styles.layerOrderSidebar}>
        <div className={styles.controls}>
          <button className={styles.readButton} onClick={() => read()}>
            {rows ? 'Refresh' : 'Read layer stack'}
          </button>
          <button className={visibleOnly ? styles.toggleOn : styles.toggleOff} onClick={handleToggleVisibleOnly}>
            Visible only
          </button>
          {rows != null && (
            <button className={styles.copyButton} onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        {rows == null && <div className={styles.hint}>Toggle layers on the map, then read the stack to check draw order.</div>}

        {rows != null && (
          <>
            <div className={violations.length ? styles.statusFail : styles.statusOk}>
              {violations.length
                ? `${violations.length} ordering violation${violations.length === 1 ? '' : 's'}`
                : `Band ordered correctly — ${bandCount} layer${bandCount === 1 ? '' : 's'}`}
            </div>

            {violations.length > 0 && (
              <div className={styles.violations}>
                {violations.map((violation, index) => (
                  <div className={styles.violation} key={index}>
                    <div className={styles.violationLine}>
                      <strong>{violation.below.id}</strong> (p={violation.below.priority}) at idx {violation.below.idx}
                    </div>
                    <div className={styles.violationCaption}>is drawn behind</div>
                    <div className={styles.violationLine}>
                      <strong>{violation.above.id}</strong> (p={violation.above.priority}) at idx {violation.above.idx}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.legend}>Top of map first · higher priority draws further back</div>

            <div className={styles.stack}>
              {rows
                .slice()
                .reverse()
                .map(row => (
                  <div className={row.visible ? styles.row : styles.rowHidden} key={`${row.idx}-${row.id}`}>
                    <span className={styles.idx}>{row.idx}</span>
                    <span className={`${styles.zone} ${styles[`zone_${row.zone}`]}`}>{ZONE_LABELS[row.zone]}</span>
                    <span className={styles.priority}>{row.priority == null ? '—' : row.priority}</span>
                    <span className={styles.id} title={row.id}>
                      {row.id}
                    </span>
                  </div>
                ))}
            </div>
          </>
        )}
      </SidebarBody>
    </>
  )
}

LayerOrderSidebar.propTypes = {
  layerOrderEnabled: PropTypes.bool,
  mapRef: PropTypes.object
}

export default connect(model => {
  return {
    layerOrderEnabled: model.app.config.LAYER_ORDER_ENABLED === true,
    mapRef: model.map.mapRef
  }
})(LayerOrderSidebar)

function Unavailable(props) {
  return (
    <>
      <SidebarDetailHeader title='Layer Order' />
      <SidebarBody>
        <div className={styles.error}>{props.message}</div>
      </SidebarBody>
    </>
  )
}

Unavailable.propTypes = {
  message: PropTypes.string
}
